import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  loadGenericFilterOperators,
  loadGenericFilterParams,
  loadGenericFilterParamValuesWithoutRedux,
  loadPagedGenericFilterParamValuesWithoutRedux
} from 'features/genericFilters/actionCreators';
import { getGenericFilterParams, getGenericFilterOperators } from 'features/genericFilters/selectors';
import GenericFilterComposer, { setInitialData } from '@ts-private/generic-filter-composer';
import { set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import FlexBox from 'components/common/FlexBox';
import { Typography } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';

// Prevents re-renders from normalizr creating new object references for unchanged data.
// Re-renders only when loading state or item count actually changes.
const contentEqual = (prev, next) =>
  prev.loading === next.loading && prev.loaded === next.loaded && prev.content.length === next.content.length;

const SublimitFiltersComponent = ({ sublimitId, condition, filters, context, onSave, canWrite }) => {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const { content: genericFilterParams } = useSelector(state => getGenericFilterParams(state, context), contentEqual);
  const { content: genericFilterOperators } = useSelector(state => getGenericFilterOperators(state, context), contentEqual);
  const nameSaved = useRef();

  const composerId = useMemo(() => `composer-${sublimitId}`, [sublimitId]);
  const actionsSlotId = useMemo(() => `actions-slot-${sublimitId}`, [sublimitId]);

  const getComposerData = useCallback(
    (newCondition, newFilters) => {
      return {
        withTemplate: false,
        templateId: null,
        template: {
          phrase: newCondition || condition || null,
          conditions: (newFilters || filters || []).map(filter => ({
            id: filter.filterId,
            orderNo: filter.orderNo,
            paramId: filter.paramId,
            paramName: filter.paramName,
            operatorId: filter.operatorId,
            operatorName: filter.operatorName,
            value: filter.filterValue,
            valueId: filter.filterValueId,
            values: (filter.filterValues || []).map(value => ({
              value: value.filterValue,
              valueId: value.filterValueId
            }))
          }))
        }
      };
    },
    [condition, filters]
  );

  const [initialized, setInitialized] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(getComposerData());

  const resetComposerData = useCallback(
    (newCondition, newFilters) => {
      const composerData = getComposerData(newCondition, newFilters);
      setInitialData(composerData, composerId);
      resetLens({ ...composerData, disabled: !canWrite });
    },
    [canWrite, composerId, getComposerData, resetLens]
  );

  useEffect(() => {
    if (initialized) return;
    resetComposerData();
    setInitialized(true);
  }, [initialized, resetComposerData]);

  // Syncs disabled state when canWrite arrives after initialization.
  const lensRef = useRef(lens);
  lensRef.current = lens;
  useEffect(() => {
    if (!initialized) return;
    set(lensRef.current.disabled)(!canWrite);
  }, [canWrite, initialized]);

  const loadParamValues = useCallback(
    paramId =>
      reduxDispatch(loadGenericFilterParamValuesWithoutRedux(paramId)).then(result =>
        (result || []).map(({ id, name }) => ({ value: id, label: name }))
      ),
    [reduxDispatch]
  );

  const loadPagedParamValues = useCallback(
    paramId => (name, _, additional) => {
      const page = name === nameSaved.current ? additional?.page ?? 1 : 1;
      nameSaved.current = name;
      return reduxDispatch(loadPagedGenericFilterParamValuesWithoutRedux(paramId, name, page)).then(data => ({
        loadedOptions: data.map(({ id, name }) => ({ value: id, label: name })),
        more: data.length > 0,
        additional: { page: page + 1 }
      }));
    },
    [reduxDispatch]
  );

  useEffect(() => {
    if (!canWrite) return;
    reduxDispatch(loadGenericFilterOperators(context));
    reduxDispatch(loadGenericFilterParams(context));
  }, [canWrite, context, reduxDispatch]);

  const handleSave = data => {
    set(lens.disabled)(true);
    onSave(
      'FiltersUpdate',
      {
        context,
        sublimitId,
        condition: data.template.phrase,
        filters: data.template.conditions.map(condition => ({
          sublimitId,
          orderNo: condition.orderNo,
          paramId: condition.paramId,
          operatorId: condition.operatorId,
          filterValue: condition.value,
          filterValueId: condition.valueId,
          filterValues: condition.values.map(value => ({
            filterValue: value.value,
            filterValueId: value.valueId
          }))
        }))
      },
      resetComposerData
    );
  };

  return (
    <FlexBox direction='column' m={2}>
      <FlexBox justifyContent='space-between' px={1}>
        <Typography fontWeight='bold' variant='subtitle1' color='primary'>
          {t('GenericFilterComposer.Conditions')}
        </Typography>
        <FlexBox gap={0.5} id={actionsSlotId}></FlexBox>
      </FlexBox>
      <GenericFilterComposer
        id={composerId}
        lens={lens}
        dirtyInfo={dirtyInfo}
        resetLens={resetLens}
        parameters={genericFilterParams}
        operators={genericFilterOperators}
        onSave={handleSave}
        loadValues={loadParamValues}
        loadPagedValues={loadPagedParamValues}
        withTemplateGeneralData
        actionsSlotId={actionsSlotId}
      />
    </FlexBox>
  );
};

SublimitFiltersComponent.propTypes = {
  sublimitId: PropTypes.number,
  condition: PropTypes.string,
  filters: PropTypes.array,
  context: PropTypes.string,
  onSave: PropTypes.func,
  canWrite: PropTypes.bool
};

export default memo(SublimitFiltersComponent);
